import React, { Component } from 'react';
import { withTheme } from 'styled-components';
import { connect } from 'react-redux';
import _ from 'underscore';
import { Select } from 'antd';
import { Image } from '../../../../Reusable/Image';
import { Text } from '../../../../Reusable/Text';
import { Flex, FlexColumn } from '../../../../Reusable/Container';
import { LabeledInput } from '../../../../Reusable/Input';
import { Button, TextButton } from '../../../../Reusable/Button';
import { ExpenseCategory } from '../../../../Reusable/ExpenseCategory';
import { toCurrency, toNumberStr } from '../../../../../Utils/util';
import TableView from '../../../../Reusable/TableView';
import { ErrorAlert } from '../../../../Reusable/Alert';
import { apiPOSTReq } from '../../../../../Utils/api';
import environment from '../../../../../environment';
import DeleteIcon from '../../../../../Images/delete.svg';
import Add from '../../../../../Images/add.svg';
import { cloneDeep } from 'lodash';

const { Option } = Select;

// Priority map for expense categories
const priorityMap = {
    "Accounts Payable": 2,
    "Accounts Receivable": 3,
    "Bank": 4,
    "Cost of Goods Sold": 5,
    "Credit Card": 6,
    "Equity": 7,
    "Expense": 0,
    "Fixed Asset": 8,
    "Income": 9,
    "Long Term Liability": 10,
    "Other Asset": 11,
    "Other Current Asset": 12,
    "Other Current Liability": 13,
    "Other Expense": 1,
    "Other Income": 14,
};

class EditExpenseCategorization extends Component {
    constructor(props) {
        super(props);
        this.state = {
            bill: cloneDeep(this.props.bill || { line: [] }),
            categories: this.props.categories || [],
            editMode: this.props.editMode !== undefined ? this.props.editMode : true,
            categoriesLoading: !this.props.categories || this.props.categories.length === 0,
        };
    }

    componentDidMount() {
        // Initialize line items
        const { bill } = this.state;
        if (bill.line) {
            bill.line.forEach((line, i) => {
                if (!line.itemId) line.itemId = i;
            });
        }

        // If categories weren't provided as props, fetch them
        if (!this.props.categories || this.props.categories.length === 0) {
            this.fetchCOA({});
        }
    }

    componentDidUpdate(prevProps) {
        // Update bill if it changes
        if (prevProps.bill !== this.props.bill) {
            this.setState({ bill: this.props.bill });
        }

        // Update categories if they're provided as props and change
        if (prevProps.categories !== this.props.categories && this.props.categories) {
            this.setState({ categories: this.props.categories });
        }

        // Update editMode if it changes
        if (prevProps.editMode !== this.props.editMode) {
            this.setState({ editMode: this.props.editMode });
        }
    }

    /**
     * Fetches Chart of Accounts (categories) from the API
     * @param {Object} options - Options for the fetch request
     * @param {string} options.searchStr - Optional search string to filter categories
     */
    fetchCOA = ({ searchStr }) => {
        const headers = {
            AionCurrentBiz: this.props.store.BusinessUniqueKey,
            AionAuth: this.props.store.UAM.encryptedAuthHeader
        };

        const body = {
            "BusinessId": this.props.store.BusinessUniqueKey,
            "size": 1000,
            "page": 0,
            "ActiveRecords": true,
            "sortFieldName": "Code",
            "sortDirection": "ASC"
        };

        if (searchStr) {
            body.searchStr = searchStr;
        }

        this.setState({ categoriesLoading: true });

        apiPOSTReq(`${environment.bbBaseUrl}/bk/getCategories`, headers, body, (err, resp) => {
            try {
                const data = resp;
                if (data.result) {
                    const customCOAList = data.customCOAList.filter(x =>
                        x.coatype !== 'Accounts Payable' && x.coatype !== 'Accounts Receivable'
                    );

                    let categories = (data.systemCOAList || []).concat(customCOAList);
                    this.setState({
                        categories: categories ? categories.sort((a, b) => {
                            if (priorityMap[a.coatype] < priorityMap[b.coatype]) return -1;
                            else return 1;
                        }).map(x => ({
                            ...x,
                            key: x.id,
                        })) : [],
                        categoriesLoading: false
                    });
                } else {
                    this.setState({ categoriesLoading: false });
                    throw Error(data.error || data.responseMessage || "Sorry we had trouble processing your request. Please try again later");
                }
            } catch (error) {
                this.setState({ categoriesLoading: false });
                console.log("ERRR getCategories", error, err, resp);
                ErrorAlert({ description: error.message });
            }
        });
    }

    /**
     * Handles search functionality for Chart of Accounts
     * @param {string} value - The search string
     */
    handleSearchCOA = value => {
        this.fetchCOA({ searchStr: value });
    }

    /**
     * Adds a new line item row to the bill
     */
    addLineItemRow = () => {
        let bill = { ...this.state.bill };
        let billLine = bill.line || [];

        bill.line = billLine.concat([{
            itemId: billLine.length,
            lineNum: billLine.length,
            unsavedItem: true
        }]);

        this.setState({ bill }, () => {
            // Call the parent's onBillUpdate if provided
            if (this.props.onBillUpdate) {
                this.props.onBillUpdate(bill);
            }
        });
    }

    /**
     * Removes a line item row from the bill
     * @param {number} id - The index of the line item to remove
     */
    removeLineItemRow = (id) => {
        let bill = { ...this.state.bill };
        if (!bill.line || bill.line.length <= 1) {
            bill.line = [];
            this.setState({ bill }, () => {
                if (this.props.onBillUpdate) {
                    this.props.onBillUpdate(bill);
                }
            });
            return;
        }

        bill.line.splice(id, 1);
        this.setState({ bill }, () => {
            if (this.props.onBillUpdate) {
                this.props.onBillUpdate(bill);
            }
            this.updateTotals();
        });
    }

    /**
     * Handles selection of an expense category for a line item
     * @param {string} value - The selected category ID
     * @param {number} index - The index of the line item
     */
    handleSelectCategory = (value, index) => {
        let bill = { ...this.state.bill };
        const { categories } = this.state;
        const category = categories.find(item => item.id === value);

        const line = bill.line || [];
        if (line.length <= index) {
            return;
        }

        const selectedItem = line[index];
        if (selectedItem) {
            selectedItem.accountBasedExpenseLineDetail = {
                accountRef: {
                    value: category.id,
                    name: category.categoryName,
                }
            };
        } else {
            line.push({
                itemId: index,
                accountBasedExpenseLineDetail: {
                    accountRef: {
                        value: category.id,
                        name: category.categoryName,
                    }
                }
            });
        }

        this.setState({ bill }, () => {
            if (this.props.onBillUpdate) {
                this.props.onBillUpdate(bill);
            }
            this.updateTotals();
        });
    }

    /**
     * Handles text changes for line item descriptions
     * @param {Event} event - The input event
     * @param {number} index - The index of the line item
     */
    handleLineItemTextChange = (event, index) => {
        let bill = { ...this.state.bill };
        const line = bill.line || [];
        if (line.length <= index) {
            return;
        }

        const selectedItem = line[index];
        if (selectedItem) {
            selectedItem[event.target.id] = event.target.value;
        } else {
            line.push({
                itemId: index,
                [event.target.id]: event.target.value,
            });
        }

        this.setState({ bill }, () => {
            if (this.props.onBillUpdate) {
                this.props.onBillUpdate(bill);
            }
            this.updateTotals();
        });
    }

    /**
     * Handles amount changes for line items
     * @param {string} value - The amount value
     * @param {number} index - The index of the line item
     */
    handleLineItemAmountTextChange = (value, index) => {
        let bill = { ...this.state.bill };
        const line = bill.line || [];
        if (line.length <= index) {
            return;
        }

        const selectedItem = line[index];
        if (selectedItem) {
            selectedItem.amount = value;
        } else {
            line.push({
                itemId: index,
                amount: value,
            });
        }

        this.setState({ bill }, () => {
            if (this.props.onBillUpdate) {
                this.props.onBillUpdate(bill);
            }
            this.updateTotals();
        });
    }

    /**
     * Calculates the total amount of all line items
     * @returns {Object} The total amount
     */
    getLineItemTotals = () => {
        const { bill } = this.state;
        const reducer = (accumulator, item) => accumulator + parseFloat(item.amount || 0);
        const total = (bill.line || []).reduce(reducer, 0);
        return { totalAmt: total };
    }

    /**
     * Updates the total and balance in the bill based on line items
     */
    updateTotals = () => {
        let bill = { ...this.state.bill };
        const totals = this.getLineItemTotals();
        bill.totalAmt = totals.totalAmt || 0;
        bill.balance = bill.totalAmt;

        this.setState({ bill }, () => {
            if (this.props.onBillUpdate) {
                this.props.onBillUpdate(bill);
            }
        });
    }

    /**
     * Checks if any line items are missing expense categories
     * @returns {boolean} True if at least one line item has a category but not all line items do
     */
    checkForIncompleteCOA = () => {
        const { editMode, bill } = this.state;
        const filteredCOAs = (bill.line || []).filter(line =>
            line.accountBasedExpenseLineDetail ? line.accountBasedExpenseLineDetail.accountRef : false
        );
        // Return true if all line items have not been categorized and at least one should be categorized
        return (editMode || ((filteredCOAs.length != bill.line.length) && filteredCOAs.length > 0));
    }

    /**
     * Applies the same expense category to all line items
     * @param {Object} coa - The category reference to apply to all line items
     */
    applyCOAToAll = (coa) => {
        let bill = { ...this.state.bill };
        (bill.line || []).forEach(line => {
            if (line.accountBasedExpenseLineDetail) line.accountBasedExpenseLineDetail.accountRef = coa;
            else line.accountBasedExpenseLineDetail = { accountRef: coa };
        });

        this.setState({ bill }, () => {
            if (this.props.onBillUpdate) {
                this.props.onBillUpdate(bill);
            }
        });
    }

    /**
     * Gets the column configuration for the line items table
     * @returns {Array} The column configuration
     */
    getColumns = () => {
        const { categories, editMode, bill } = this.state;
        const { theme } = this.props;

        const columns = [
            {
                title: 'Expense Account',
                dataIndex: 'category',
                width: 260,
                render: (category, lineItem, i) => {
                    const { accountBasedExpenseLineDetail } = lineItem;
                    const coa = accountBasedExpenseLineDetail ? accountBasedExpenseLineDetail.accountRef || {} : {};
                    return (
                        <div>
                            {
                                (editMode || !coa.value) ?
                                    <LabeledInput
                                        nomargin
                                        id="category"
                                        key="category"
                                        type="select"
                                        placeholder="Select account"
                                        defaultValue={coa.name}
                                        onChange={e => this.handleSelectCategory(e, i)}
                                        // onSearch={this.handleSearchCOA}
                                        noBorder={!editMode}
                                        showSearch
                                        filterOption={(input, option) => {
                                            return option.name.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                                        }}
                                        getPopupContainer={(triggerNode) => triggerNode.parentNode}
                                        className='parent-node-select-dropdown-menu'
                                    >
                                        {
                                            categories && categories.filter(category => !category.coatype.includes('Liability')).map(category => (
                                                <Option key={category.id} id="category" name={category.categoryName} value={category.id}>
                                                    <ExpenseCategory selected={coa.value == category.id} category={category} hideCategory={true} theme={theme} />
                                                </Option>
                                            ))
                                        }
                                    </LabeledInput>
                                    :
                                    <LabeledInput
                                        nomargin
                                        id="category"
                                        key={"category" + coa.name}
                                        type={"read-only"}
                                        className="no-left-padding"
                                        value={coa.name}
                                    />
                            }
                            {
                                // Show this option if there is more than one line
                                (coa.value) && (i == 0 && bill.line.length > 1) && this.checkForIncompleteCOA() &&
                                <Text light><a onClick={() => this.applyCOAToAll(coa)}>Apply this</a> to all line items</Text>
                            }
                        </div>
                    );
                }
            },
            {
                title: 'Description',
                dataIndex: 'description',
                render: (description, lineItem, i) => (
                    <div style={{ flexGrow: 1 }}>
                        {
                            editMode ?
                                <LabeledInput
                                    nomargin
                                    type={'text-area'}
                                    id="description"
                                    key={i + "description"}
                                    placeholder="Add text"
                                    autoSize={{ minRows: 1, maxRows: 3 }}
                                    defaultValue={description || ''}
                                    onBlur={(e) => this.handleLineItemTextChange(e, i)}
                                    minHeight='42px'
                                />
                                :
                                <LabeledInput
                                    nomargin
                                    type={"read-only"}
                                    key={i + "description"}
                                    value={description || ''}
                                    minHeight='42px'
                                />
                        }
                    </div>
                )
            },
            {
                title: 'Amount',
                dataIndex: 'amount',
                align: 'right',
                width: '185px',
                render: (amount, lineItem, i) => (
                    editMode ?
                        <LabeledInput
                            nomargin
                            id="amount"
                            type="amount"
                            key={i + "amount"}
                            placeholder="5000"
                            value={amount}
                            onBlur={e => {
                                const fVal = toNumberStr(e.target.value);
                                this.handleLineItemAmountTextChange(fVal, i);
                            }}
                            onPressEnter={e => {
                                const fVal = toNumberStr(e.target.value);
                                this.handleLineItemAmountTextChange(fVal, i);
                            }}
                        />
                        :
                        <LabeledInput
                            nomargin
                            type={"read-only"}
                            key={i + "amount"}
                            prefixText="$"
                            alignRight
                            value={`$${toCurrency(amount)}`}
                        />
                )
            }
        ];

        // Add delete column only in edit mode
        if (editMode) {
            columns.push({
                title: '',
                dataIndex: 'action',
                render: (action, lineItem, i) => (
                    bill.line.length > 1 &&
                    <img
                        width='24px'
                        height='24px'
                        style={{ cursor: 'pointer' }}
                        src={DeleteIcon}
                        onClick={() => this.removeLineItemRow(i)}
                    />
                ),
                width: '24px'
            });
        }

        return columns;
    }

    render() {
        const { bill, editMode, categoriesLoading } = this.state;
        const { titleText, descText, showTotal } = this.props;

        return (
            <FlexColumn gap='16px' style={{ marginTop: '24px', paddingBottom: '24px' }}>
                <TableView
                    id="line-item-table"
                    key={"line-item-table" + (bill.line || []).length}
                    tableLayout='auto'
                    columns={this.getColumns()}
                    dataSource={bill.line}
                    rowKey={(record) => record.itemId || Math.random()}
                    borderView
                    loading={categoriesLoading}
                />

                {editMode && (
                    <TextButton
                        onClick={() => this.addLineItemRow()}
                        margin="0"
                        rightIcon={<Image src={Add} />}
                        text="ADD LINE ITEM"
                    />
                )}

                {showTotal && (
                    <FlexColumn centerVertically right style={{ marginRight: 24, marginTop: 8 }} gap='8px'>
                        <Flex centerHorizontally gap='4px'>
                            <Text right caption>TOTAL</Text>
                            <Text right weight='500' style={{ minWidth: 127 }}>{`$ ${toCurrency(this.getLineItemTotals().totalAmt)}`}</Text>
                        </Flex>
                    </FlexColumn>
                )}

                <FlexColumn left>
                    <Button
                        solid 
                        onClick={() =>  {
                            this.props.updateBillVendor({ bill: bill, loadStep: "Edit" });
                        }} text={"UPDATE"} 
                    />
                </FlexColumn>
                
            </FlexColumn>
        );
    }
}

// Default props
EditExpenseCategorization.defaultProps = {
    bill: { line: [] },
    categories: null,
    editMode: true,
    showTotal: true,
    titleText: null,
};

function mapStateToProps(state) {
    return {
        store: state.aionAppReducer
    };
}

function mapDispatchToProps(dispatch) {
    return {
        dispatch
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(withTheme(EditExpenseCategorization));